const NAME_PLATE_TYPES = [
  {
    key: 'wood',
    href: '/s/judaica/nameplates?pub_j_materials=has_all%3Awood',
    title: 'Wood Name Plates',
  },
  {
    key: 'jerusalem_stone',
    href: '/s/judaica/nameplates?pub_j_materials=has_all%3Astone',
    title: 'Jerusalem Stone Name Plates',
  },
  {
    key: 'acrylic',
    href: '/s/judaica/nameplates?pub_j_materials=has_all%3Aacrylic',
    title: 'Acrylic Name Plates',
  },
  {
    key: 'hand_painted',
    href: '/s/judaica/nameplates?pub_j_materials=has_all%3Ahandpainted',
    title: 'Hand-Painted Name Plates',
  },
];

const getSchema = rootURL => {
  const namePlatesURL = `${rootURL}/s/judaica/nameplates`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${namePlatesURL}#webpage`,
      url: namePlatesURL,
      name: 'Jewish Door Name Plates',
      description:
        'Custom Jewish door name plates available for purchase — Hebrew, English, or bilingual. Personalized family signs made to order by independent Jewish artists.',
      isPartOf: { '@id': `${rootURL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Jewish Door Name Plates' },
        { '@type': 'Thing', name: 'Hebrew Door Signs' },
        { '@type': 'Thing', name: 'Personalized Jewish Gifts' },
        { '@type': 'Thing', name: 'Jewish Housewarming Gifts' },
        { '@type': 'Thing', name: 'Hebrew Name Plates' },
      ],
      publisher: { '@id': `${rootURL}/#organization` },
      breadcrumb: { '@id': `${namePlatesURL}#breadcrumb` },
      mainEntity: { '@id': `${namePlatesURL}#faq` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${namePlatesURL}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: rootURL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Judaica',
          item: `${rootURL}/s/judaica`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Jewish Door Name Plates',
          item: namePlatesURL,
        },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${namePlatesURL}#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a Hebrew door name plate?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A Hebrew door name plate is a personalized sign placed at the entrance of a home, displaying the family name in Hebrew, English, or both. Most are designed for doors and entryways, though some styles can also be displayed on a shelf or desktop using a stand.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can a Hebrew name plate be personalized with our family name?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Most name plates on Joodaic are made to order and can be personalized with your family name, a Hebrew phrase, or both. Details can be submitted at checkout or by messaging the artist.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between a Hebrew and a bilingual name plate?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A Hebrew name plate displays the name in Hebrew only. A bilingual plate includes both Hebrew and English, often chosen when the sign will be visible to guests who may not read Hebrew.',
          },
        },
        {
          '@type': 'Question',
          name: 'What size name plate works for a front door?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most door name plates are roughly 10–30 cm wide, depending on the shape, material, and layout. Smaller plaques work well near a mezuzah or doorframe, while wider pieces suit doors or entry areas where the name should be easier to read. Many artists offer multiple sizes or can adjust dimensions for a specific door.',
          },
        },
        {
          '@type': 'Question',
          name: 'What materials are name plates made from?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Name plates are available in a range of materials, including hand-painted designs, Jerusalem stone, acrylic, wood, and metal. The material affects both the look and durability, especially for outdoor use. Each listing specifies the material and finish.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are these name plates suitable for outdoor use?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'It depends on the material. Metal, acrylic, sealed wood, and stone options are typically suitable for outdoor doors, while more delicate materials may be better for indoor use. Each listing specifies this, and artists can advise if you\'re unsure.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are Jewish name plates a good housewarming gift?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. A personalized door name plate is one of the most common Jewish housewarming gifts. It marks a new home in a way that\'s personal and lasting. Many people pair it with a mezuzah case for a meaningful entrance.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I customize a name plate?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most name plates on Joodaic are made to order, so customization is part of the process. You can add personalization details in the message field at checkout, or message the artist through their storefront before placing an order. This allows you to confirm name, Hebrew spelling, language, size, and finish.',
          },
        },
      ],
    },
  ];
};

const namePlatesSeoConfig = {
  key: 'name_plates',
  // Matches both slug variants (nameplates and name_plates) for safety
  match: ({ listingType, activeJudaicaCollection }) =>
    listingType === 'judaica' &&
    (activeJudaicaCollection?.includes('name_plates') ||
      activeJudaicaCollection?.includes('nameplates')),
  accentColor: '--colorJudaica',
  links: NAME_PLATE_TYPES,
  title: 'Jewish Door Name Plates | Hebrew & English Family Signs | Joodaic',
  description:
    'Custom Jewish door name plates available for purchase — Hebrew, English, or bilingual. Personalized family signs made to order by independent Jewish artists.',
  getSchema,
  intro: {
    heading: 'Jewish Door Name Plates',
    tagline:
      'Personalized name plates for the door — in Hebrew, English, or both — made for your home, not pulled off a shelf.',
    collapsedHeading: 'Door Name Plates',
    bodyParagraphs: [
      'Most people come here looking for something simple: a name at the entrance. Something that feels like this is our home — not just another door.',
      'Some are moving in. Others are bringing a gift that should actually last. A name plate tends to do that. It goes up, and it stays.',
      "Each piece here is made to order by a Jewish artist. You choose the name, the language, and the tone — and if you don't see exactly what you want, you can have it made.",
    ],
    linksLabel: 'Browse Name Plates by Type',
  },
  bottomSections: [
    {
      id: 'language',
      type: 'prose',
      heading: 'Hebrew, English, or both?',
      paragraphs: [
        'Choosing the language is usually the first decision.',
        'Hebrew name plates feel traditional and rooted. English name plates are clean and easy for any guest to read. Bilingual options combine both and are the most common choice for shared or guest-facing spaces.',
      ],
    },
    {
      id: 'door_placement',
      title: 'Choosing a name plate for the door',
      paragraphs: ['A few practical things make a difference:'],
      list: [
        'Size — most plates range between 10–30 cm wide. Smaller plaques work near a mezuzah or doorframe, while wider pieces suit doors where the name should be clearly visible.',
        'Material — hand-painted pieces feel more personal and expressive. Jerusalem stone carries weight and texture. Acrylic and metal are cleaner and more modern. Wood sits somewhere in between.',
        'Script and layout — some styles emphasize readability, others lean more decorative. The right choice depends on where the piece will be placed and how prominent you want it to feel.',
      ],
      paragraphsAfter: [
        'Many homes pair a name plate with a <a href="/s/judaica/mezuzas">mezuzah case</a> for a complete entrance.',
      ],
    },
    {
      id: 'made_to_order',
      title: 'Made to order for your family',
      paragraphs: [
        "Most name plates on Joodaic are not stock items. They're made to order.",
        'You can usually customize:',
      ],
      list: [
        'Family name',
        'Hebrew spelling',
        'Language: Hebrew, English, or both',
        'Size and layout',
        'Finish or material',
      ],
      paragraphsAfter: [
        "If something specific is missing, artists are often open to adjusting the design.",
      ],
    },
    {
      id: 'gifts',
      title: 'Name plates as Jewish gifts',
      paragraphs: [
        'A door name plate is one of the most common Jewish housewarming gifts.',
        "It marks a new home in a way that feels personal and lasting — something that becomes part of the space, not just something placed on a shelf.",
        "It's also commonly given for weddings or moves, especially when paired with a mezuzah case.",
      ],
    },
    {
      id: 'related',
      title: 'Other Judaica for the entrance',
      paragraphs: ['You can also browse:'],
      list: [
        '<a href="/s/judaica/mezuzas">Mezuzah cases</a>',
        '<a href="/s/judaica">Judaica collection</a>',
      ],
    },
    {
      id: 'artists',
      title: 'Made by independent Jewish artists',
      paragraphs: [
        'Every piece on Joodaic comes from an independent Jewish artist.',
        'That means variation in style, material, and approach — from clean modern plaques to more decorative or traditional designs — rather than a single uniform look.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: [
    {
      question: 'What is a Hebrew door name plate?',
      answer: [
        'A Hebrew door name plate is a personalized sign placed at the entrance of a home, displaying the family name in Hebrew, English, or both. Most are designed for doors and entryways, though some styles can also be displayed on a shelf or desktop using a stand.',
      ],
    },
    {
      question: 'Can a Hebrew name plate be personalized with our family name?',
      answer: [
        'Yes. Most name plates on Joodaic are made to order and can be personalized with your family name, a Hebrew phrase, or both.',
        'Details can be submitted at checkout or by messaging the artist.',
      ],
    },
    {
      question: 'What is the difference between a Hebrew and a bilingual name plate?',
      answer: [
        'A Hebrew name plate displays the name in Hebrew only. A bilingual plate includes both Hebrew and English, often chosen when the sign will be visible to guests who may not read Hebrew.',
      ],
    },
    {
      question: 'What size name plate works for a front door?',
      answer: [
        'Most door name plates are roughly 10–30 cm wide, depending on the shape, material, and layout. Smaller plaques work well near a mezuzah or doorframe, while wider pieces suit doors or entry areas where the name should be easier to read.',
        'Many artists offer multiple sizes or can adjust dimensions for a specific door.',
      ],
    },
    {
      question: 'What materials are name plates made from?',
      answer: [
        'Name plates are available in a range of materials, including hand-painted designs, Jerusalem stone, acrylic, wood, and metal. The material affects both the look and durability, especially for outdoor use.',
        'Each listing specifies the material and finish.',
      ],
    },
    {
      question: 'Are these name plates suitable for outdoor use?',
      answer: [
        "It depends on the material. Metal, acrylic, sealed wood, and stone options are typically suitable for outdoor doors, while more delicate materials may be better for indoor use.",
        "Each listing specifies this, and artists can advise if you're unsure.",
      ],
    },
    {
      question: 'Are Jewish name plates a good housewarming gift?',
      answer: [
        'Yes. A personalized door name plate is one of the most common Jewish housewarming gifts. It marks a new home in a way that\'s personal and lasting.',
        'Many people pair it with a mezuzah case for a meaningful entrance.',
      ],
    },
    {
      question: 'How do I customize a name plate?',
      answer: [
        'Most name plates on Joodaic are made to order, so customization is part of the process. You can add personalization details in the message field at checkout, or message the artist through their storefront before placing an order.',
        'This allows you to confirm name, Hebrew spelling, language, size, and finish.',
      ],
    },
  ],
};

export default namePlatesSeoConfig;
